import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Info, RotateCcw, Heart, LogOut, User, Trash2 } from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [mustDoLimit, setMustDoLimit] = useState(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    api.getSettings().then((s) => setMustDoLimit(s.must_do_limit)).catch(() => setMustDoLimit(3));
  }, []);

  const saveLimit = async (n) => {
    const previous = mustDoLimit;
    setMustDoLimit(n);
    try {
      await api.updateSettings({ must_do_limit: n });
      toast.success(`Must-do limit set to ${n}.`);
    } catch (e) {
      setMustDoLimit(previous);
      toast.error("Couldn't save the limit. Try again.");
    }
  };

  const reseed = async () => {
    setBusy(true);
    try {
      await api.reseed();
      toast.success("Sample data reset.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell title="Settings" subtitle="Keep it simple.">
      <div className="space-y-4">
        <div className="bg-zinc-100 rounded-2xl p-5 shadow-md">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#365458] shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-zinc-900">About this app</h3>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                Next Right Thing helps you capture the mess, sort it, and pick one
                meaningful next action. No streaks, no guilt, no clutter.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-100 rounded-2xl p-5 shadow-md">
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-[#365458] shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-zinc-900">AI brain dump sorting</h3>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                Your brain dump is sorted by AI (OpenAI <span className="font-medium">gpt-4o-mini</span>) into
                organized, usable items — capped at your must-do limit, ideas sent to the parking lot,
                and big tasks broken into smaller next actions.
              </p>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                <span className="font-medium">Setup:</span> uses
                <code className="text-xs bg-zinc-200 px-1 py-0.5 rounded ml-1">OPENAI_API_KEY</code> in
                <code className="text-xs bg-zinc-200 px-1 py-0.5 rounded ml-1">backend/.env</code>.
                Swap the model with
                <code className="text-xs bg-zinc-200 px-1 py-0.5 rounded ml-1">OPENAI_MODEL</code> or update
                <code className="text-xs bg-zinc-200 px-1 py-0.5 rounded ml-1">backend/ai_brain_dump.py</code>.
                If the AI request fails, the app automatically falls back to a simple rule-based sorter, so it always works.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-100 rounded-2xl p-5 shadow-md">
          <h3 className="font-semibold text-zinc-900 mb-1">Must-do limit</h3>
          <p className="text-sm text-zinc-600 mb-4">
            How many items can appear in "Must do today". Keep it small — that's the point.
          </p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                data-testid={`mustdo-limit-${n}`}
                onClick={() => saveLimit(n)}
                className={`flex-1 py-3 rounded-xl text-base font-semibold transition-colors ${
                  mustDoLimit === n ? "bg-[#365458] text-white" : "bg-zinc-200 text-zinc-600 hover:bg-zinc-300"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-zinc-100 rounded-2xl p-5 shadow-md">
          <h3 className="font-semibold text-zinc-900 mb-1">Trash</h3>
          <p className="text-sm text-zinc-600 mb-4">
            Deleted tasks and ideas wait here. Restore them, or delete them forever.
          </p>
          <Button
            data-testid="settings-trash-button"
            onClick={() => navigate("/trash")}
            variant="outline"
            className="w-full h-12 rounded-2xl bg-transparent border-zinc-300 text-zinc-700 hover:bg-zinc-200"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Open trash
          </Button>
        </div>

        <div className="bg-zinc-100 rounded-2xl p-5 shadow-md">
          <h3 className="font-semibold text-zinc-900 mb-1">Reset sample data</h3>
          <p className="text-sm text-zinc-600 mb-4">
            Restore the original example tasks and parking lot ideas.
          </p>
          <Button
            data-testid="reseed-button"
            onClick={reseed}
            disabled={busy}
            variant="outline"
            className="w-full h-12 rounded-2xl bg-transparent border-zinc-300 text-zinc-700 hover:bg-zinc-200"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> {busy ? "Resetting…" : "Reset to sample data"}
          </Button>
        </div>

        <div className="bg-zinc-100 rounded-2xl p-5 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#365458]/12 flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-[#365458]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-zinc-900">Signed in</h3>
              <p className="text-sm text-zinc-600 truncate" data-testid="settings-user-email">{user?.email}</p>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={logout}
            variant="outline"
            className="w-full h-12 mt-4 rounded-2xl bg-transparent border-zinc-300 text-[#991B1B] hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>

        <p className="text-center text-xs text-zinc-600 pt-2">Next Right Thing · private · v1</p>
      </div>
    </PageShell>
  );
}
